import { supabase } from '../lib/supabase';

/**
 * Get or create a conversation between two users
 */
export const getOrCreateConversation = async (participantOneId, participantTwoId) => {
  try {
    // Check if conversation already exists (either direction)
    const { data: existing, error: searchError } = await supabase?.from('conversations')?.select('*')?.or(`and(participant_one_id.eq.${participantOneId},participant_two_id.eq.${participantTwoId}),and(participant_one_id.eq.${participantTwoId},participant_two_id.eq.${participantOneId})`)?.maybeSingle();

    if (searchError && searchError?.code !== 'PGRST116') {
      throw searchError;
    }

    if (existing) {
      return { data: existing, error: null };
    }

    // Create new conversation
    const { data, error } = await supabase?.from('conversations')?.insert({
        participant_one_id: participantOneId,
        participant_two_id: participantTwoId
      })?.select()?.single();

    return { data, error };
  } catch (error) {
    console.error('Error in getOrCreateConversation:', error);
    return { data: null, error };
  }
};

/**
 * Get all conversations for a user with participant details and last message
 */
export const getUserConversations = async (userId) => {
  try {
    const { data, error } = await supabase?.from('conversations')?.select(`
        *,
        participant_one:user_profiles!conversations_participant_one_id_fkey(id, full_name, avatar_url, role),
        participant_two:user_profiles!conversations_participant_two_id_fkey(id, full_name, avatar_url, role),
        messages(id, content, created_at, sender_id, status)
      `)?.or(`participant_one_id.eq.${userId},participant_two_id.eq.${userId}`)?.order('last_message_at', { ascending: false });

    if (error) throw error;

    // Transform data to include other participant and last message
    const conversations = data?.map(conv => {
      const otherParticipant = conv?.participant_one_id === userId 
        ? conv?.participant_two 
        : conv?.participant_one;
      
      const lastMessage = conv?.messages?.sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      )?.[0];

      const unreadCount = conv?.messages?.filter(
        msg => msg?.sender_id !== userId && msg?.status !== 'read'
      )?.length || 0;

      return {
        id: conv?.id,
        otherParticipant,
        lastMessage: lastMessage?.content || '',
        lastMessageTime: lastMessage?.created_at || conv?.last_message_at,
        unreadCount,
        createdAt: conv?.created_at
      };
    }) || [];

    return { data: conversations, error: null };
  } catch (error) {
    console.error('Error fetching conversations:', error);
    return { data: [], error };
  }
};

/**
 * Get messages for a conversation
 */
export const getConversationMessages = async (conversationId) => {
  try {
    const { data, error } = await supabase?.from('messages')?.select(`
        *,
        sender:user_profiles!messages_sender_id_fkey(id, full_name, avatar_url),
        attachments:message_attachments(*)
      `)?.eq('conversation_id', conversationId)?.order('created_at', { ascending: true });

    if (error) throw error;

    return { data: data || [], error: null };
  } catch (error) {
    console.error('Error fetching messages:', error);
    return { data: [], error };
  }
};

/**
 * Send a message
 */
export const sendMessage = async (conversationId, senderId, content, isProposal = false, proposalData = null) => {
  try {
    const { data, error } = await supabase?.from('messages')?.insert({
        conversation_id: conversationId,
        sender_id: senderId,
        content,
        is_proposal: isProposal,
        proposal_data: proposalData,
        status: 'sent'
      })?.select(`
        *,
        sender:user_profiles!messages_sender_id_fkey(id, full_name, avatar_url)
      `)?.single();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('Error sending message:', error);
    return { data: null, error };
  }
};

/**
 * Mark messages as read
 */
export const markMessagesAsRead = async (conversationId, userId) => {
  try {
    const { error } = await supabase?.from('messages')?.update({ 
        status: 'read',
        read_at: new Date()?.toISOString()
      })?.eq('conversation_id', conversationId)?.neq('sender_id', userId)?.in('status', ['sent', 'delivered']);

    if (error) throw error;

    return { error: null };
  } catch (error) {
    console.error('Error marking messages as read:', error);
    return { error };
  }
};

/**
 * Upload file attachment
 */
export const uploadMessageFile = async (file, userId) => {
  try {
    const fileExt = file?.name?.split('.')?.pop();
    const fileName = `${Date.now()}-${Math.random()?.toString(36)?.substring(7)}.${fileExt}`;
    const filePath = `${userId}/${fileName}`;

    const { data, error } = await supabase?.storage?.from('message-files')?.upload(filePath, file, {
        cacheControl: '3600',
        upsert: false
      });

    if (error) throw error;

    return { 
      data: {
        path: data?.path,
        fileName: file?.name,
        fileType: file?.type,
        fileSize: file?.size
      }, 
      error: null 
    };
  } catch (error) {
    console.error('Error uploading file:', error);
    return { data: null, error };
  }
};

/**
 * Get signed URL for file download
 */
export const getMessageFileUrl = async (filePath) => {
  try {
    const { data, error } = await supabase?.storage?.from('message-files')?.createSignedUrl(filePath, 3600); // 1 hour expiry

    if (error) throw error;

    return { data: data?.signedUrl, error: null };
  } catch (error) {
    console.error('Error getting file URL:', error);
    return { data: null, error };
  }
};

/**
 * Create message attachment record
 */
export const createMessageAttachment = async (messageId, fileData) => {
  try {
    const { data, error } = await supabase?.from('message_attachments')?.insert({
        message_id: messageId,
        file_name: fileData?.fileName,
        file_path: fileData?.path,
        file_type: fileData?.fileType,
        file_size: fileData?.fileSize
      })?.select()?.single();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('Error creating attachment:', error);
    return { data: null, error };
  }
};

/**
 * Send message with file attachment
 */
export const sendMessageWithFile = async (conversationId, senderId, content, file) => {
  try {
    // Upload file first
    const { data: fileData, error: uploadError } = await uploadMessageFile(file, senderId);
    if (uploadError) throw uploadError;

    // Create message
    const { data: message, error: messageError } = await sendMessage(
      conversationId,
      senderId,
      content || `Sent a file: ${file?.name}`
    );
    if (messageError) throw messageError;

    // Create attachment record
    const { error: attachmentError } = await createMessageAttachment(message?.id, fileData);
    if (attachmentError) throw attachmentError;

    return { data: message, error: null };
  } catch (error) {
    console.error('Error sending message with file:', error);
    return { data: null, error };
  }
};

/**
 * Subscribe to new messages in a conversation
 */
export const subscribeToConversationMessages = (conversationId, callback) => {
  const channel = supabase?.channel(`messages-${conversationId}`)?.on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'messages',
        filter: `conversation_id=eq.${conversationId}`
      },
      async (payload) => {
        // Fetch full message with sender details
        const { data } = await supabase?.from('messages')?.select(`
            *,
            sender:user_profiles!messages_sender_id_fkey(id, full_name, avatar_url),
            attachments:message_attachments(*)
          `)?.eq('id', payload?.new?.id)?.single();

        if (data) {
          callback(data);
        }
      }
    )?.on(
      'postgres_changes',
      {
        event: 'UPDATE',
        schema: 'public',
        table: 'messages',
        filter: `conversation_id=eq.${conversationId}`
      },
      (payload) => {
        callback(payload?.new, 'UPDATE');
      }
    )?.subscribe();

  return {
    unsubscribe: () => {
      supabase?.removeChannel(channel);
    }
  };
};

/**
 * Subscribe to conversation list updates
 */
export const subscribeToConversations = (userId, callback) => {
  const channel = supabase?.channel('conversations-updates')?.on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'conversations',
        filter: `participant_one_id=eq.${userId}`
      },
      () => {
        callback();
      }
    )?.on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'conversations',
        filter: `participant_two_id=eq.${userId}`
      },
      () => {
        callback();
      }
    )?.on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'messages'
      },
      () => {
        callback();
      }
    )?.subscribe();

  return {
    unsubscribe: () => {
      supabase?.removeChannel(channel);
    }
  };
};

/**
 * Search messages in a conversation
 */
export const searchMessages = async (conversationId, searchTerm) => {
  try {
    const { data, error } = await supabase?.from('messages')?.select(`
        *,
        sender:user_profiles!messages_sender_id_fkey(id, full_name, avatar_url)
      `)?.eq('conversation_id', conversationId)?.ilike('content', `%${searchTerm}%`)?.order('created_at', { ascending: false });

    if (error) throw error;

    return { data: data || [], error: null };
  } catch (error) {
    console.error('Error searching messages:', error);
    return { data: [], error };
  }
};

/**
 * Update proposal status
 */
export const updateProposalStatus = async (messageId, status) => {
  try {
    const { data, error } = await supabase?.from('messages')?.update({
        proposal_data: supabase?.rpc('jsonb_set', {
          target: 'proposal_data',
          path: '{status}',
          new_value: JSON.stringify(status)
        })
      })?.eq('id', messageId)?.select()?.single();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('Error updating proposal:', error);
    return { data: null, error };
  }
};

/**
 * Delete a message
 */
export const deleteMessage = async (messageId) => {
  try {
    const { error } = await supabase?.from('messages')?.delete()?.eq('id', messageId);

    if (error) throw error;

    return { error: null };
  } catch (error) {
    console.error('Error deleting message:', error);
    return { error };
  }
};